/**
 * FloatingMenu - Sticky global menu button in bottom right corner
 * Opens on hover, contains same entries as navbar hamburger menu (filters + categories)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { categoriesAPI } from '@/lib/api';
import {
    Menu, X, ChevronDown, ChevronRight,
    TrendingUp, Gavel, ArrowLeftRight,
    Medal, Coins, Swords, Box, Package,
    Heart, Grid3X3, Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// Icon mapping for categories
const categoryIcons = {
    'cat_faleristica': Medal,
    'cat_numismatica': Coins,
    'cat_militare': Swords,
    'cat_diverse': Box,
    'faleristica': Medal,
    'numismatica': Coins,
    'efecte-militare': Swords,
    'diverse': Box,
    'Medal': Medal,
    'Coins': Coins,
    'Shield': Swords,
    'Package': Box,
};

// Default categories
const defaultCategories = [
    {
        category_id: 'cat_faleristica',
        name: 'Faleristică',
        slug: 'faleristica',
        icon: 'Medal',
        subcategories: [
            { subcategory_id: 'sub_ordine', name: 'Ordine', slug: 'ordine' },
            { subcategory_id: 'sub_medalii_f', name: 'Medalii', slug: 'medalii' },
            { subcategory_id: 'sub_titluri', name: 'Titluri', slug: 'titluri' },
            { subcategory_id: 'sub_insigne', name: 'Insigne', slug: 'insigne' },
            { subcategory_id: 'sub_brevete', name: 'Brevete', slug: 'brevete' },
            { subcategory_id: 'sub_diplome', name: 'Diplome', slug: 'diplome' },
            { subcategory_id: 'sub_loturi_f', name: 'Loturi', slug: 'loturi' },
            { subcategory_id: 'sub_diverse_f', name: 'Diverse', slug: 'diverse' }
        ]
    },
    {
        category_id: 'cat_numismatica',
        name: 'Numismatică',
        slug: 'numismatica',
        icon: 'Coins',
        subcategories: [
            { subcategory_id: 'sub_monede', name: 'Monede', slug: 'monede' },
            { subcategory_id: 'sub_bancnote', name: 'Bancnote', slug: 'bancnote' },
            { subcategory_id: 'sub_medalii_n', name: 'Medalii', slug: 'medalii' },
            { subcategory_id: 'sub_jetoane', name: 'Jetoane', slug: 'jetoane' },
            { subcategory_id: 'sub_token', name: 'Token', slug: 'token' },
            { subcategory_id: 'sub_loturi_n', name: 'Loturi', slug: 'loturi' }
        ]
    },
    {
        category_id: 'cat_militare',
        name: 'Militare',
        slug: 'efecte-militare',
        icon: 'Shield',
        subcategories: [
            { subcategory_id: 'sub_uniforme', name: 'Uniforme', slug: 'uniforme' },
            { subcategory_id: 'sub_epoleti', name: 'Epoleți', slug: 'epoleti' },
            { subcategory_id: 'sub_arme', name: 'Arme', slug: 'arme' },
            { subcategory_id: 'sub_loturi_m', name: 'Loturi', slug: 'loturi' },
            { subcategory_id: 'sub_diverse_m', name: 'Diverse', slug: 'diverse' }
        ]
    },
    {
        category_id: 'cat_diverse',
        name: 'Diverse',
        slug: 'diverse',
        icon: 'Package',
        subcategories: [
            { subcategory_id: 'sub_pionieri', name: 'Pionieri', slug: 'pionieri' },
            { subcategory_id: 'sub_poze', name: 'Poze', slug: 'poze' },
            { subcategory_id: 'sub_carti', name: 'Cărți', slug: 'carti' },
            { subcategory_id: 'sub_tablouri', name: 'Tablouri', slug: 'tablouri' },
            { subcategory_id: 'sub_bijuterii', name: 'Bijuterii', slug: 'bijuterii' },
            { subcategory_id: 'sub_loturi_d', name: 'Loturi', slug: 'loturi' }
        ]
    }
];

// Expandable category item
const CategoryItem = ({ category, onClose }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const IconComponent = categoryIcons[category.icon] || categoryIcons[category.category_id] || Box;
    const subcategories = category.subcategories || [];

    const handleCategoryClick = () => {
        // Clear any cached category state to prevent flash
        sessionStorage.removeItem(`infinite_scroll_category_${category.slug}`);
        onClose();
    };

    return (
        <div>
            <div className="flex items-center">
                <Link
                    to={`/category/${category.slug}`}
                    className="flex-1 flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg"
                    onClick={handleCategoryClick}
                >
                    <IconComponent className="w-4 h-4 text-primary" />
                    <span className="font-medium text-sm">{category.name}</span>
                </Link>
                {subcategories.length > 0 && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-1.5 bg-transparent hover:bg-gray-200 rounded-lg transition-colors mr-2"
                    >
                        <ChevronDown className={cn(
                            "w-4 h-4 text-muted-foreground transition-transform",
                            isExpanded && "rotate-180"
                        )} />
                    </button>
                )}
            </div>
            
            {isExpanded && subcategories.length > 0 && (
                <div className="ml-4 pl-3 border-l border-gray-200/50 space-y-0.5 pb-1">
                    {subcategories.map((sub) => {
                        const subSlug = typeof sub === 'string' ? sub.toLowerCase() : sub.slug;
                        const subName = typeof sub === 'string' ? sub : sub.name;
                        const subKey = typeof sub === 'string' ? sub : sub.subcategory_id;
                        
                        return (
                            <Link
                                key={subKey}
                                to={`/category/${category.slug}/${subSlug}`}
                                className="block px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg"
                                onClick={() => {
                                    sessionStorage.removeItem(`infinite_scroll_category_${category.slug}_${subSlug}`);
                                    onClose();
                                }}
                            >
                                {subName}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const FloatingMenu = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [categories, setCategories] = useState(defaultCategories);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    
    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                if (res.data && res.data.length > 0) {
                    setCategories(res.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);
    
    // Detect if device supports touch (mobile)
    const isTouchDevice = () => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };
    
    // Handle mouse enter - open menu (desktop only)
    const handleMouseEnter = () => {
        if (isTouchDevice()) return; // Skip on mobile
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setIsOpen(true);
    };
    
    // Handle mouse leave - close menu after delay (desktop only)
    const handleMouseLeave = () => {
        if (isTouchDevice()) return; // Skip on mobile
        hoverTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    };
    
    // Handle button click - toggle menu
    const handleButtonClick = (e) => {
        e.stopPropagation();
        const newState = !isOpen;
        setIsOpen(newState);
        // Dispatch event to close other menus when this one opens
        if (newState) {
            window.dispatchEvent(new CustomEvent('floatingMenuOpened'));
        }
    };
    
    // Listen for other menus opening and close this one
    useEffect(() => {
        const handleOtherMenuOpened = () => {
            setIsOpen(false);
        };
        window.addEventListener('avatarMenuOpened', handleOtherMenuOpened);
        window.addEventListener('adminFloatingMenuOpened', handleOtherMenuOpened);
        return () => {
            window.removeEventListener('avatarMenuOpened', handleOtherMenuOpened);
            window.removeEventListener('adminFloatingMenuOpened', handleOtherMenuOpened);
        };
    }, []);
    
    // Close menu on navigation
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);
    
    // Close menu when clicking outside (for mobile)
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        
        if (isOpen) {
            document.addEventListener('touchstart', handleClickOutside);
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);
    
    const closeMenu = () => {
        setIsOpen(false);
    };
    
    // Check if we're on the messages page
    const isMessagesPage = location.pathname.startsWith('/messages');
    
    // Check if mobile (using window width)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Hide completely on mobile when on messages page
    if (isMessagesPage && isMobile) {
        return null;
    }
    
    return (
        <div 
            ref={menuRef}
            className={cn(
                "fixed right-6 z-[9999]",
                isMessagesPage ? "bottom-20" : "bottom-6"
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid="floating-menu"
        >
            {/* Menu Panel */}
            <div className={cn(
                "absolute bottom-16 right-0 w-72 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 origin-bottom-right bg-white/95 backdrop-blur-sm",
                isOpen 
                    ? "opacity-100 scale-100 translate-y-0" 
                    : "opacity-0 scale-95 translate-y-4 pointer-events-none"
            )}>
                {/* Content */}
                <div className="max-h-[80vh] overflow-y-auto py-3">
                    {/* Listează piesă - for all users, navigate to create-listing or register */}
                    <div className="px-2 mb-2">
                        <button
                            onClick={() => { 
                                navigate(isAuthenticated ? '/create-listing' : '/login'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 bg-primary text-white hover:bg-primary/90 transition-colors rounded-lg font-medium text-sm"
                            data-testid="floating-create-listing-link"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Listează piesă</span>
                        </button>
                    </div>
                    
                    {/* FILTRE Section */}
                    <div className="px-4 mb-2 mt-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Filtre</h3>
                    </div>
                    
                    <div className="border-t border-gray-200/50 mx-4 my-2"></div>
                    
                    <nav className="space-y-0.5 px-2 mb-2">
                        <button
                            onClick={() => { 
                                sessionStorage.setItem('nav_to_all_sales', 'true');
                                navigate('/all-sales'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg text-left"
                            data-testid="floating-sales-link"
                        >
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-sm">Preț fix</span>
                        </button>
                        
                        <button
                            onClick={() => { 
                                sessionStorage.setItem('nav_to_all_auctions', 'true');
                                navigate('/all-auctions'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg text-left"
                            data-testid="floating-auctions-link"
                        >
                            <Gavel className="w-4 h-4 text-blue-500" />
                            <span className="font-medium text-sm">Licitații</span>
                        </button>
                        
                        <button
                            onClick={() => { 
                                sessionStorage.setItem('nav_to_trades', 'true');
                                navigate('/trades'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg text-left"
                            data-testid="floating-trades-link"
                        >
                            <ArrowLeftRight className="w-4 h-4 text-orange-500" />
                            <span className="font-medium text-sm">Schimburi</span>
                        </button>
                    </nav>
                    
                    {/* CATEGORII Section */}
                    <div className="px-4 mb-2 mt-3">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Categorii</h3>
                    </div>
                    
                    <nav className="space-y-0.5 px-2 mb-2">
                        {/* Individual categories */}
                        {categories.map((category) => (
                            <CategoryItem 
                                key={category.category_id || category.id} 
                                category={category} 
                                onClose={closeMenu} 
                            />
                        ))}
                    </nav>
                    
                    {/* Separator */}
                    <div className="border-t border-gray-200 mx-4 my-2"></div>
                    
                    <nav className="space-y-0.5 px-2">
                        <button
                            onClick={() => { 
                                localStorage.setItem('lastListingView', 'my-domains');
                                sessionStorage.setItem('nav_to_my_domains', 'true');
                                navigate(isAuthenticated ? '/my-domains' : '/login'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg text-left"
                            data-testid="floating-my-domains-link"
                        >
                            <Heart className="w-4 h-4 text-red-500" />
                            <span className="font-medium text-sm">Categoriile mele</span>
                        </button>
                        
                        <button
                            onClick={() => { 
                                localStorage.setItem('lastListingView', 'all-listings');
                                sessionStorage.setItem('nav_to_all_listings', 'true');
                                navigate('/all-listings'); 
                                closeMenu(); 
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg text-left"
                            data-testid="floating-all-link"
                        >
                            <Grid3X3 className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-sm">Toate piesele</span>
                        </button>
                    </nav>
                </div>
            </div>
            
            {/* Floating Button - transparent background with only border and icon opaque */}
            <button
                onClick={handleButtonClick}
                className={cn(
                    "h-14 w-14 rounded-full transition-all duration-300 flex items-center justify-center backdrop-blur-sm",
                    isOpen 
                        ? "bg-primary text-white shadow-lg ring-2 ring-primary ring-offset-2" 
                        : "bg-white/20 border-2 border-primary shadow-lg hover:shadow-xl"
                )}
                data-testid="floating-menu-button"
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <Menu className="w-6 h-6 text-primary" />
                )}
            </button>
        </div>
    );
};

export default FloatingMenu;
